// src/PageDiaporama.js
import { useState, useEffect } from 'react';
import { collection, query, where, orderBy, onSnapshot, doc, getDoc, updateDoc } from 'firebase/firestore';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { db, auth } from './firebase';
import PageLogin from './PageLogin';

const DIAPORAMA_ID = 'wGkKHFfvUaEbDjM3Sxw0';
const DEFAUT_DUREE_AFFICHAGE = 10000; // en millisecondes, 10s par défaut


function PageDiaporama() {
  const [usager, setUsager] = useState(null);
  const [verificationTerminee, setVerificationTerminee] = useState(false);
  const [medias, setMedias] = useState([]);
  const [indexActuel, setIndexActuel] = useState(0);
  const [televersementActif, setTeleversementActif] = useState(true);
  const [sonActive, setSonActive] = useState(false);
  const [dureeAffichage, setDureeAffichage] = useState(DEFAUT_DUREE_AFFICHAGE); 

  const basculerTeleversement = async () => {
    const refDiaporama = doc(db, '50iemeFrancineYvon', DIAPORAMA_ID);
    await updateDoc(refDiaporama, {
      televersementActif: !televersementActif
    });
  };

  const changerDuree = async (nouvelleDureeSecondes) => {
    const dureeMs = nouvelleDureeSecondes * 1000;
    setDureeAffichage(dureeMs); // mise à jour locale immédiate
    await updateDoc(doc(db, '50iemeFrancineYvon', DIAPORAMA_ID), {
      dureeAffichage: dureeMs
    });
  };

  const allerPrecedent = () => {
    setIndexActuel((precedent) => (precedent - 1 + medias.length) % medias.length);
  };

  const allerSuivant = () => {
    setIndexActuel((precedent) => (precedent + 1) % medias.length);
  };

  useEffect(() => {
    if (!usager) return;

    const refDiaporama = doc(db, '50iemeFrancineYvon', DIAPORAMA_ID);
    const desabonner = onSnapshot(refDiaporama, (snapshot) => {
      if (snapshot.exists()) {
        setTeleversementActif(snapshot.data().televersementActif);
      }
    });

    return () => desabonner();
  }, [usager]);

  // Écoute l'état de connexion (usager connecté ou non)
  useEffect(() => {
    const desabonner = onAuthStateChanged(auth, (usagerConnecte) => {
      setUsager(usagerConnecte);
      setVerificationTerminee(true);
    });
    return () => desabonner();
  }, []);

  // Écoute en temps réel de la collection "medias" (seulement si connecté)
  useEffect(() => {
    if (!usager) return;

    const q = query(
      collection(db, 'medias'),
      where('diaporamaId', '==', DIAPORAMA_ID),
      orderBy('dateAjout', 'asc')
    );

    const desabonner = onSnapshot(q, (snapshot) => {
      const listeMedias = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data()
      }));
      setMedias(listeMedias);
    });

    return () => desabonner();
  }, [usager]);

  // Défilement automatique
  useEffect(() => {
    if (medias.length === 0) return;

    const minuteur = setTimeout(() => {
      setIndexActuel((precedent) => (precedent + 1) % medias.length);
    }, dureeAffichage);

    return () => clearTimeout(minuteur);
  }, [indexActuel, medias]);

  useEffect(() => {
    if (!usager) return;

    const refDiaporama = doc(db, '50iemeFrancineYvon', DIAPORAMA_ID);

    const desabonner = onSnapshot(refDiaporama, (snap) => {
      if (snap.exists() && snap.data().dureeAffichage) {
        setDureeAffichage(snap.data().dureeAffichage);
      }
    });

    return () => desabonner();
  }, [usager]);

  // Tant qu'on ne sait pas encore si l'usager est connecté, on attend
  if (!verificationTerminee) {
    return <p>Chargement...</p>;
  }

  // Pas connecté → afficher la page de connexion
  if (!usager) {
    return <PageLogin />;
  }

  // Connecté mais aucun média encore
  if (medias.length === 0) {
    return (
      <div>
        <p>En attente de photos et vidéos...</p>
        <button onClick={basculerTeleversement}>
          {televersementActif ? 'Désactiver le téléversement' : 'Activer le téléversement'}
        </button>
        <button onClick={() => signOut(auth)}>Se déconnecter</button>
      </div>
    );
  }

  const mediaActuel = medias[indexActuel];

  return (
    <div style={{ position: 'relative', width: '100%', height: '100vh', backgroundColor: 'black' }}>
      {mediaActuel.type === 'photo' ? (
        <img
          src={mediaActuel.url}
          alt=""
          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
        />
      ) : (
        <video
          src={mediaActuel.url}
          autoPlay
          muted={!sonActive}
          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
        />
      )}

      <div style={{
        position: 'absolute',
        bottom: 20,
        left: 20,
        color: 'white',
        backgroundColor: 'rgba(0,0,0,0.6)',
        padding: '10px 20px',
        borderRadius: '8px'
      }}>
        <strong>{mediaActuel.nomAuteur}</strong>
        {mediaActuel.message && <p style={{ margin: 0 }}>{mediaActuel.message}</p>}
      </div>
      <button
        onClick={() => signOut(auth)}
        style={{ position: 'absolute', top: 10, right: 10 }}
      >
        Se déconnecter
      </button>

      {!sonActive && (
        <button
          onClick={() => setSonActive(true)}
          style={{ position: 'absolute', top: 10, left: 10 }}
        >
          🔊 Activer le son
        </button>
      )}
      <div style={{ position: 'absolute', top: 10, right: 10, display: 'flex', gap: '10px' }}>
        <button onClick={basculerTeleversement}>
          {televersementActif ? 'Désactiver upload' : 'Activer upload'}
        </button>
        <button onClick={() => signOut(auth)}>Se déconnecter</button>
      </div>
      <div style={{
        position: 'absolute',
        bottom: 20,
        right: 20,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: '10px'
      }}>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={allerPrecedent}>⬅️ Précédent</button>
          <button onClick={allerSuivant}>Prochain ➡️</button>
        </div>

        <div style={{
          color: 'white',
          backgroundColor: 'rgba(0,0,0,0.6)',
          padding: '8px 12px',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <span>{dureeAffichage / 1000}s</span>
          <input
            type="range"
            min="5"
            max="30"
            step="1"
            value={dureeAffichage / 1000}
            onChange={(e) => changerDuree(Number(e.target.value))}
          />
        </div>
      </div>

    </div>

  );
}



export default PageDiaporama;